import {
  OrderMeal,
  OrderMealEntree,
  OrderMealItem,
  BasicOrderMealItem,
  PreConfiguredMeal,
  RecentOrFavoriteOrderMeal,
  OrderMealRequestMeal,
  MenuMetadataForChannel,
  PreConfiguredMealEntreeContent,
  OrderMealEntreeContents,
  MenuItemLookupResult,
  Menu,
  NonFoodItem,
} from '@/models';
import reduce from 'lodash/reduce';

export interface AnalyticsProduct {
  name?: string;
  id: string;
  price?: number;
  category: string;
  quantity?: number;
  /** filling, but I'm told GTM can't handle names */
  dimension6?: string;

  /** toppings, but I'm told GTM can't handle names */
  dimension7?: string;
}

export function mapMealItemToAnalyticsProduct(
  item: BasicOrderMealItem,
  category: 'entree' | 'side' | 'drink',
  activeMenu: Menu | null,
  price?: number,
  menuItemLookupMap?: Map<string, MenuItemLookupResult>,
  lifestyleMeals?: PreConfiguredMeal[]
): AnalyticsProduct {
  let name: string = item.menuItemName;
  if (category == 'entree' && activeMenu && activeMenu.entrees) {
    const foundMenuItem = activeMenu.entrees.find(x => x.itemId == item.menuItemId);
    if (foundMenuItem) {
      name = foundMenuItem.itemType;
    }
    if (lifestyleMeals) {
      const itemAsOrderMealItem = <OrderMealItem>item;
      if (itemAsOrderMealItem && itemAsOrderMealItem.preconfiguredItemName) {
        const isLifestyleMeal = (lifestyleMeals && lifestyleMeals.some(p => p.entree.itemId === item.menuItemId && p.mealName == itemAsOrderMealItem.preconfiguredItemName));
        if (isLifestyleMeal) {
          name = itemAsOrderMealItem.preconfiguredItemName;
        }
      }
    }
  }
  const product: AnalyticsProduct = {
    name,
    id: item.menuItemId,
    quantity: item.quantity,
    category,
  };

  if (price) {
    product.price = price;
  }
  if (category == 'entree' && menuItemLookupMap) {
    product.dimension6 = getFillingByEntreeItemId(item.menuItemId, menuItemLookupMap);
  }

  return product;
}

export function mapNonFoodItemToAnalyticsProduct(
  item: NonFoodItem,
  category: 'nonFoodItem',
  price?: number,
): AnalyticsProduct {

  const product: AnalyticsProduct = {
    name: item.menuItemName,
    id: item.menuItemId,
    quantity: item.quantity,
    category,
  };

  if (price) {
    product.price = price;
  }

  return product;
}

export function convertMealsToAnalyticsProducts(
  meals: OrderMeal[],
  metadata: MenuMetadataForChannel,
  menuItemLookupMap: Map<string, MenuItemLookupResult>,
  activeMenu: Menu | null,
  lifestyleMeals?: PreConfiguredMeal[]
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (meals) {
    meals.forEach(meal => {
      if (meal.entrees) {
        meal.entrees.forEach((entree: OrderMealEntree) => {

          const product = mapMealItemToAnalyticsProduct(entree, 'entree', activeMenu, entree.entreeTotalAmount, menuItemLookupMap, lifestyleMeals);

          const toppings = generateOrderMealToppingsList(entree.contents, metadata);
          if (toppings) {
            product.dimension7 = toppings;
          }
          products.push(product);
        });
      }

      if (meal.sides) {
        meal.sides.forEach((side: OrderMealItem) => {
          const product = mapMealItemToAnalyticsProduct(side, 'side', activeMenu, side.itemTotalAmount);
          products.push(product);
        });
      }

      if (meal.drinks) {
        meal.drinks.forEach((drink: OrderMealItem) => {
          const product = mapMealItemToAnalyticsProduct(drink, 'drink', activeMenu, drink.itemTotalAmount);
          products.push(product);
        });
      }
    });
  }

  return products;
}

export function convertNonFoodItemsToAnalyticsProducts(
  nonFoodItems: NonFoodItem[]
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (nonFoodItems) {
    nonFoodItems.forEach(nonFoodItem => {
      const product = mapNonFoodItemToAnalyticsProduct(nonFoodItem, 'nonFoodItem', nonFoodItem.itemUnitPrice);
      products.push(product);
    });
  }

  return products;
}

export function convertPreconfiguredMealToAnalyticsProducts(
  meal: PreConfiguredMeal,
  metadata: MenuMetadataForChannel,
  menuItemLookupMap: Map<string, MenuItemLookupResult>,
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (meal.entree) {
    const toppings = generatePreconfiguredToppingsList(meal.entree.contents, metadata);
    const product: AnalyticsProduct = {
      id: meal.entree.itemId,
      name: meal.entree.entreeName,
      price: meal.entree.unitPrice,
      category: 'entree',
      quantity: meal.entree.quantity,
      dimension6: getFillingByEntreeItemId(meal.entree.itemId, menuItemLookupMap),
    };
    if (toppings) {
      product.dimension7 = toppings;
    }
    products.push();
  }

  if (meal.sides) {
    meal.sides.forEach(side => products.push({ id: side.itemId, category: 'side' }));
  }

  if (meal.drinks) {
    meal.drinks.forEach(drink => products.push({ id: drink.itemId, category: 'drink' }));
  }

  return products;
}

export function convertMealRequestOrRecentOrFavoriteMealToAnalyticsProducts(
  meal: RecentOrFavoriteOrderMeal | OrderMealRequestMeal,
  activeMenu: Menu | null,
): AnalyticsProduct[] {
  const products: AnalyticsProduct[] = [];

  if (meal.entrees) {
    meal.entrees.forEach((entree: BasicOrderMealItem) => {
      const product = mapMealItemToAnalyticsProduct(entree, 'entree', activeMenu);
      // TODO Generate toppings list from these two meal types
      products.push(product);
    });
  }

  if (meal.sides) {
    meal.sides.forEach((side: BasicOrderMealItem) => products.push(mapMealItemToAnalyticsProduct(side, 'side', activeMenu)));
  }

  if (meal.drinks) {
    meal.drinks.forEach((drink: BasicOrderMealItem) => products.push(mapMealItemToAnalyticsProduct(drink, 'drink', activeMenu)));
  }

  return products;
}

// Returns in the format of 'Steak Burrito, Carnitas Tacos, Large Chips & Guac'
export function reduceProductsToString(products: AnalyticsProduct[]): string {
  return reduce(
    products,
    (mealsOrdered, product) =>
      product.name ? `${mealsOrdered ? `${mealsOrdered}, ` : ''}${product.category == 'entree' ? `${product.dimension6} ${product.name}` : product.name}` : mealsOrdered,
    '',
  );
}

interface StringMap {
  [key: string]: string;
}
function generatePreconfiguredToppingsList(contents: PreConfiguredMealEntreeContent[], metadata: MenuMetadataForChannel): string | undefined {
  if (!metadata.itemSections.Toppings) {
    return;
  }

  const baseObj: StringMap = {};
  // e.g. {CMG-123: Queso, CMG-456: Salsa, ...}
  const idNameToppingMap = reduce(
    contents,
    (accu, content) => {
      return { ...accu, [content.itemId]: content.itemName };
    },
    baseObj,
  );
  const toppingIdsInEntree = Object.keys(idNameToppingMap);

  return reduce(
    metadata.itemSections.Toppings.items,
    (toppings: string[], item) => {
      if (toppingIdsInEntree.includes(item.menuItemId)) {
        toppings.push(idNameToppingMap[item.menuItemId]);
      }
      return toppings;
    },
    [],
  ).join(', ');
}

function getFillingByEntreeItemId(itemId: string, menuItemLookupMap: Map<string, MenuItemLookupResult>): string | undefined {
  const menuItem = menuItemLookupMap.get(itemId);
  if (menuItem) {
    return menuItem.primaryFillingName;
  }
}

function generateOrderMealToppingsList(contents: OrderMealEntreeContents[] | null, metadata: MenuMetadataForChannel): string | undefined {
  if (!metadata.itemSections.Toppings || !contents) {
    return;
  }

  const baseObj: StringMap = {};
  // e.g. {CMG-123: Queso, CMG-456: Salsa, ...}
  const idNameToppingMap = reduce(
    contents,
    (accu, content) => {
      return { ...accu, [content.menuItemId]: content.menuItemName };
    },
    baseObj,
  );
  const toppingIdsInEntree = Object.keys(idNameToppingMap);

  // Call everything but the protein a topping for an entree if available. It should always be available, but if not, fall back to subset of toppings (excluding rice & beans)
  const toppingMeta = metadata.itemSections.OptionContentGroup ? metadata.itemSections.OptionContentGroup.items : metadata.itemSections.Toppings.items;
  return reduce(
    toppingMeta,
    (toppings: string[], item) => {
      if (toppingIdsInEntree.includes(item.menuItemId)) {
        toppings.push(idNameToppingMap[item.menuItemId]);
      }
      return toppings;
    },
    [],
  ).join(', ');
}
